import { animate, state, style, transition, trigger } from '@angular/animations';
import { DragDropModule } from '@angular/cdk/drag-drop'; // Import DragDropModule
import { CheckboxComponent } from 'src/app/shared/components/ui/checkbox/checkbox.component';

import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, EventEmitter, inject, input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppService } from 'src/app/app.service';
import { NotificationService } from 'src/app/notification.service';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable, Subscription, tap } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { EnvService } from 'src/app/env.service';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { CHUNKS_ALLOWED_FOR_AUDIO_GEN, CHUNKS_WITH_AUDIO_PLAYER, SILENT_CHUNKS } from '../../chunk/chunk.dto';
import { ContentService } from '../../content.service';
import { ContentStateService } from '../../content-state.service';
import { Chunk } from '../chunk.dto';
import { ChunkService } from '../chunk.service';
import { CreateChunkOptionsOverlayComponent } from '../create-chunk/create-chunk-options-overlay/create-chunk-options-overlay.component';
import { CreateChunkService } from '../create-chunk/create-chunk.service';
import { LiveDictationButtonComponent } from 'src/app/shared/components/recorders/live-dictation-button/live-dictation-button.component';

@Component({
  selector: 'app-chunk-controller',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatSelectModule,
    MatChipsModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    DragDropModule,
    CheckboxComponent,
    LiveDictationButtonComponent,
  ],
  templateUrl: './chunk-controller.component.html',
  styleUrls: ['./chunk-controller.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('showHideControls', [
      state(
        'in',
        style({
          opacity: 1,
          transform: 'translateY(0)',
        })
      ),
      transition('void => *', [
        style({
          opacity: 0,
          transform: 'translateY(-100%)',
        }),
        animate('150ms ease-in'),
      ]),
      transition('* => void', [
        animate(
          '150ms ease-out',
          style({
            opacity: 0,
            transform: 'translateY(100%)',
          })
        ),
      ]),
    ]),
    trigger('slideInOut', [
      state(
        'in',
        style({
          opacity: 1,
          transform: 'translateX(0)',
        })
      ),
      transition('void => *', [
        style({
          opacity: 0,
          transform: 'translateX(-100%)',
        }),
        animate('150ms ease-in'),
      ]),
      transition('* => void', [
        animate(
          '150ms ease-out',
          style({
            opacity: 0,
            transform: 'translateX(100%)',
          })
        ),
      ]),
    ]),
  ],
})
export class ChunkControllerComponent implements OnInit, OnDestroy {
  private envService = inject(EnvService);
  private appService = inject(AppService);
  private destroyRef = inject(DestroyRef);
  private translate = inject(TranslateService);
  private overlayService = inject(DynamicOverlayService);
  private createChunkService = inject(CreateChunkService);
  private chunkService = inject(ChunkService);
  private contentState = inject(ContentStateService);
  private contentService = inject(ContentService);
  private breakpointObserver = inject(BreakpointObserver);
  private notificationService = inject(NotificationService);

  public isMobile = toSignal(this.breakpointObserver.observe('(max-width: 600px)').pipe(map((r) => r.matches)), {
    initialValue: this.breakpointObserver.isMatched('(max-width: 600px)'),
  });

  public isSaving = toSignal(this.createChunkService.savingChunk$);
  public saveSuccess = signal(false);
  public editorFocus = toSignal(this.createChunkService.editorFocus$);

  private textTypeChangeSub: Subscription;
  private playbackRateTimeout: any;
  private savingSub: Subscription;
  saveSuccessSub: Subscription;

  // --- Inputs (Signal-based) ---
  chunk = input.required<Chunk>();
  contentID = input<string>();
  courseID = input<string>();
  blogID = input<string>();
  nodelete = input<boolean>();
  isTextual = input<boolean>();
  isCode = input<boolean>();
  isMedia = input<boolean>();
  chunkAboveId = input<string>();
  chunkBelowId = input<string>();
  editorWrapper = input<ElementRef>();
  readOnly = input<boolean>(false);

  // --- Outputs ---
  @Output() play = new EventEmitter<Chunk | null>();
  @Output() pause = new EventEmitter<void>();
  @Output() playVideoAt = new EventEmitter<string>();
  @Output() editionCreated = new EventEmitter<any>();

  public playerProgress = computed<number>(() => this.contentState.playerProgress());
  public playbackRate = computed(() => this.contentState.playerState().playbackRate ?? 1);
  public isPlaying = computed(() => {
    const playerState = this.contentState.playerState();
    return playerState?.chunkId === this.chunk()?._id && playerState.status === 'playing';
  });
  public hasNextChunk = computed(() => this.contentState.hasNextChunk());
  public hasPreviousChunk = computed(() => this.contentState.hasPreviousChunk());
  public isContentEditor = computed(() => this.contentState.isContentEditor());
  private localGenerating = signal(false);
  private tick = signal(0);
  private intervalId: any;

  public generatingAudio = computed(() => {
    const tasks = this.contentState.activeTasks();
    const chunkId = this.chunk()?._id;
    const isRemoteGenerating = chunkId ? tasks[chunkId]?.audio?.status === 'generating' : false;
    const isBuffering = this.contentState.playerState().status === 'buffering' && this.contentState.playerState().chunkId === chunkId;
    return this.localGenerating() || isRemoteGenerating || isBuffering;
  });
  public hasActiveTask = computed(() => {
    const tasks = this.contentState.activeTasks();
    const chunkId = this.chunk()?._id;
    if (!chunkId || !tasks[chunkId]) return false;
    return Object.entries(tasks[chunkId]).some(
      ([taskType, task]: [string, any]) => taskType !== 'embedding' && task?.status === 'generating'
    );
  });
  public showDragIndicatorDuringLongProcessing = computed(() => {
    this.tick(); // Establish dependency on the timer
    const tasks = this.contentState.activeTasks();
    const chunkId = this.chunk()?._id;
    if (!chunkId || !tasks[chunkId]) return false;
    
    return Object.entries(tasks[chunkId]).some(([taskType, task]: [string, any]) => {
      if (taskType === 'embedding' || task?.status !== 'generating') return false;
      const updatedAtVal = task?.updatedAt;
      if (!updatedAtVal) return false;
      
      let date: Date | null = null;
      if (updatedAtVal instanceof Date) {
        date = updatedAtVal;
      } else if (typeof updatedAtVal.toDate === 'function') {
        date = updatedAtVal.toDate();
      } else if (typeof updatedAtVal.seconds === 'number') {
        date = new Date(updatedAtVal.seconds * 1000);
      } else if (typeof updatedAtVal === 'string' || typeof updatedAtVal === 'number') {
        date = new Date(updatedAtVal);
      }
      
      if (!date) return false;
      const elapsedMs = Date.now() - date.getTime();
      return elapsedMs > 60000; // 1 minute
    });
  });
  public isHighlighted = computed(() => this.contentState.highlightedChunkId() === this.chunk()?._id);

  // Multi-select state
  public isMultiSelectMode = computed(() => this.contentState.isMultiSelectMode());
  public isSelected = computed(() => this.contentState.selectedChunks().includes(this.chunk()?._id));

  // New state and methods
  public showPlaybackRateOptions = signal(false);
  public allPlaybackRates = [0.5, 0.7, 1, 1.2, 1.5, 1.7, 2]; // All possible rates

  // --- State from Services (as Signals) ---
  appCreateMode = toSignal(this.appService.createMode$, { initialValue: false });

  // --- Component State ---
  selectedLanguage = this.translate.currentLang;
  selectedVoiceLanguage = this.translate.currentLang;
  voiceOverParams: any = {};
  audioUploadParams: any = {};
  chunkEditions: any[] = [];
  selectedTextType: string;

  // --- Computed Values ---
  chunkLanguages = computed(() => {
    const currentLang = this.translate.currentLang;
    const langs = {
      fr: [
        { value: 'fr', viewValue: 'Français' },
        { value: 'en', viewValue: 'Anglais' },
      ],
      en: [
        { value: 'fr', viewValue: 'French' },
        { value: 'en', viewValue: 'English' },
      ],
    };
    return langs[currentLang] || langs['en'];
  });

  constructor() {
    this.textTypeChangeSub = this.createChunkService.textTypeChange$.subscribe((type) => {
      this.selectedTextType = type;
    });

    this.savingSub = this.createChunkService.savingChunk$.pipe(filter((saving) => saving)).subscribe(() => {
      this.contentState.setLoading({ status: 'loading', reason: 'save-chunk', message: this.translate.instant('Loader.Saving') });
    });

    this.saveSuccessSub = this.createChunkService.saveSuccess$.pipe(filter((success) => success)).subscribe(() => {
      this.saveSuccess.set(true);
      this.contentState.setLoading({ status: 'success', reason: 'save-chunk', message: this.translate.instant('Loader.Saved') });
      setTimeout(() => {
        this.saveSuccess.set(false);
        this.contentState.setLoading({ status: 'idle' });
      }, 2000);
    });
  }

  ngOnInit() {
    this.voiceOverParams = this.chunk();
    this.filterChunkEditions();
    this.selectVoiceOptions();
    this.intervalId = setInterval(() => {
      this.tick.set(this.tick() + 1);
    }, 10000); // Check every 10 seconds
  }

  retrySync(event: Event) {
    event.stopPropagation();
    const currentChunk = this.chunk();
    if (!currentChunk || !currentChunk.chunkParams) return;
    
    // reset sync state to pending locally first
    this.contentState.updateChunkLocally(currentChunk._id, { syncState: 'pending' });
    
    // retry
    this.chunkService.createChunk({ chunks: [currentChunk.chunkParams] }).subscribe();
  }

  // --- Event Handlers ---
  onPlay(): void {
    const audioToPlay = this.chunk()?.audios?.find((audio) => audio._id === this.translate.currentLang);
    const audioIsUpToDate = audioToPlay && this.chunk().editions.find((ed) => ed.language === this.translate.currentLang)?.__v === audioToPlay.__v;
    const hasNativeAudio = CHUNKS_WITH_AUDIO_PLAYER.includes(this.chunk().type);
    const isSilentChunk = SILENT_CHUNKS.includes(this.chunk().type);
    const isAudioGenerationAllowed = CHUNKS_ALLOWED_FOR_AUDIO_GEN.includes(this.chunk().type);
    const isEmptyChunk = ['<p></p><p></p>', '<p></p>', ''].includes(this.chunk().editions.find((ed) => ed.language === this.translate.currentLang)?.content || '');

    if (hasNativeAudio || audioIsUpToDate || isSilentChunk || isEmptyChunk) {
      this.play.emit(this.chunk());
    } else if (isAudioGenerationAllowed) {
      this.generateAudio(this.chunk())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((chunk) => {
          this.play.emit(chunk);
        });
    } else {
      // Handle unsupported chunk type: right now we do nothing
    }
  }

  private generateAudio(chunk: Chunk): Observable<Chunk> {
    this.localGenerating.set(true);
    const targetLang = this.translate.currentLang;
    const edition = chunk.editions.find((e) => e.language === targetLang);
    const targetVersion = edition?.__v || 1;

    const trigger$ = this.chunkService.generateAudio({
      chunkId: chunk._id,
      linkPrefix: this.envService.linkPrefix,
      version: targetVersion,
      editions: chunk.editions,
      audios: chunk.audios || [],
      targetLanguage: targetLang,
      contentId: this.contentID(),
    });

    const update$ = this.notificationService.newChunkReceived$.pipe(
      filter((notification) => notification && notification.chunkId === chunk._id),
      map((notification) => {
        try {
          return JSON.parse(notification.data) as Chunk;
        } catch (e) {
          return null;
        }
      }),
      filter((updatedChunk): updatedChunk is Chunk => {
        if (!updatedChunk) return false;
        const audio = updatedChunk.audios?.find((a) => a._id === targetLang);
        return !!(audio && audio.__v === targetVersion);
      }),
      take(1),
      switchMap(() => this.chunkService.getChunk(chunk._id))
    );

    return trigger$.pipe(
      switchMap(() => update$),
      tap({
        next: () => {
          this.localGenerating.set(false);
        },
        error: () => {
          this.localGenerating.set(false);
          this.contentState.setLoading({ status: 'error', message: this.translate.instant('Loader.AudioGenerationFailed'), reason: 'generate-audio' });
          setTimeout(() => this.contentState.setLoading({ status: 'idle' }), 5000);
        },
      })
    );
  }

  onPause(): void {
    this.pause.emit();
  }

  onChangePlaybackRate(): void {
    this.contentState.cyclePlaybackRate();
  }

  onNext(): void {
    this.contentState.playNextChunk();
  }

  // New methods for hover
  onMouseEnterPlaybackRate(): void {
    clearTimeout(this.playbackRateTimeout);
    this.showPlaybackRateOptions.set(true);
  }

  onMouseLeavePlaybackRate(): void {
    this.playbackRateTimeout = setTimeout(() => {
      this.showPlaybackRateOptions.set(false);
    }, 300); // 300ms delay
  }

  // New method to set a specific rate
  onSetSpecificPlaybackRate(rate: number): void {
    this.contentState.setSpecificPlaybackRate(rate);
    this.showPlaybackRateOptions.set(false); // Hide options after selection
  }

  onPrevious(): void {
    this.contentState.playPreviousChunk();
  }

  onPlayVideoAt(timestamp: string): void {
    this.playVideoAt.emit(timestamp);
  }

  handleEditionManipulationSuccessEvent(event: any): void {
    this.editionCreated.emit(event);
    const chunk = this.chunk();
    if (chunk) {
      chunk.editMode = 'seeEdition';
    }
  }

  // --- Helper Methods ---
  filterChunkEditions(language: string = this.selectedLanguage): void {
    const chunk = this.chunk();
    if (!chunk) return;

    if (language !== 'all') {
      this.chunkEditions = chunk.editions.filter((e) => e.language === (language || this.translate.currentLang));
    } else {
      this.chunkEditions = chunk.editions;
    }

    this.chunkEditions.forEach((edition) => {
      edition.isMain = chunk.mains.find((e) => e.main === edition._id) ? true : false;
    });
  }

  selectVoiceOptions(): void {
    this.audioUploadParams = {
      ...this.chunk(),
      language: this.selectedVoiceLanguage || localStorage.getItem('language'),
    };
  }

  openOptions(event: MouseEvent): void {
    event.stopPropagation();
    const trigger = event.currentTarget as HTMLElement;
    this.overlayService.open(trigger, CreateChunkOptionsOverlayComponent, {
      data: {
        chunk: this.chunk(),
        chunkAboveId: this.chunkAboveId(),
        chunkBelowId: this.chunkBelowId(),
        contentID: this.contentID(),
        courseID: this.courseID(),
        blogID: this.blogID(),
        mode: 'modify-chunk',
        context: 'controller',
        triggerRef: new ElementRef(trigger),
      },
      positioning: 'topLeft',
      overlayHeight: 280,
    });
  }

  cancelChunkCreation(): void {
    this.createChunkService.setCreateMode(false);
  }

  unhighlightChunk(event?: MouseEvent): void {
    event?.stopPropagation();
    this.contentState.setHighlightedChunkId(null);
    this.contentService.setInsertBelowChunk(null);
  }

  toggleSelection(): void {
    this.contentState.toggleChunkSelection(this.chunk()._id);
  }

  ngOnDestroy(): void {
    this.textTypeChangeSub?.unsubscribe();
    this.createChunkService.setCreateMode(false);
    this.savingSub?.unsubscribe();
    this.saveSuccessSub?.unsubscribe();
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
}
